import { Injectable, Provider } from '@angular/core';
import { DateFnsAdapter, MAT_DATE_FNS_FORMATS } from '@angular/material-date-fns-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { Locale } from 'date-fns';
import { getLocale } from '@vendasta/galaxy/utility/locale';

function isDateFnsLocale(locale: unknown): locale is Locale {
  return typeof locale === 'object' && locale !== null && 'localize' in locale;
}

/**
 * DateFnsAdapter that never ends up with a string locale: scopes that provide it
 * without a MAT_DATE_LOCALE date-fns Locale (or inherit Material's LOCALE_ID string
 * default) fall back to the user's locale instead of throwing on first format().
 *
 * The heal lives in setLocale (not the constructor) because DateFnsAdapter's
 * constructor takes no arguments — it injects MAT_DATE_LOCALE itself and passes
 * whatever it finds, string or not, straight to setLocale.
 */
@Injectable()
export class GalaxyDateAdapter extends DateFnsAdapter {
  override setLocale(locale: Locale): void {
    super.setLocale(isDateFnsLocale(locale) ? locale : getLocale());
  }
}

export const MY_DATE_FORMATS = {
  ...MAT_DATE_FNS_FORMATS,
  display: { ...MAT_DATE_FNS_FORMATS.display, dateInput: 'PPP' },
};

/**
 * The one sanctioned way to provide a Material DateAdapter in this repo. Returns the
 * adapter and its matching formats as a single unit so a merged injector can never pair
 * this locale/formats with a different adapter — the provider-merge roulette that
 * module-scope date providers cause. Use in component `providers`, route `providers`,
 * or application bootstrap; never in an NgModule.
 */
export function provideGalaxyDateAdapter(dateFormats: MatDateFormats = MAT_DATE_FNS_FORMATS): Provider[] {
  return [
    { provide: DateAdapter, useClass: GalaxyDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: dateFormats },
  ];
}
